import { History, Location, Path } from 'history';
import { observe, observable, action, makeObservable } from 'mobx';
import { createBrowserHistory } from 'history';

export const history = createBrowserHistory();

export class RouterStore {
    @observable location: string = '';

    constructor() {
        makeObservable(this);
    }

    @action
    updateLocation(location: Location): void {
        if (this.location !== location.pathname) {
            this.location = location.pathname;
            this.updateMouseflow();
            return;
        }

        this.location = location.pathname;
    }

    @action
    push(path: Path): void {
        history.push(path);
    }

    @action
    replace(path: Path): void {
        history.replace(path);
    }

    @action
    go(n: number): void {
        history.go(n);
    }

    @action
    goBack(): void {
        history.goBack();
    }

    @action
    goForward(): void {
        history.goForward();
    }

    @action
    private updateMouseflow(): void {
        window._mfq = window._mfq || [];
        window._mfq.push(['newPageView', this.location]);
    }
}

export type SimpleLocationListener = (location: string, action: string) => void;

export interface IHistorySubscriber extends History {
    subscribe(listener?: SimpleLocationListener): () => void;
    unsubscribe(): void;
}

export const syncHistoryWithStore = (historyToSync: History, store: RouterStore): IHistorySubscriber => {
    const handleLocationChange = (location: Location) => {
        store.updateLocation(location);
    };

    const unsubscribeFromHistory = historyToSync.listen(handleLocationChange);
    handleLocationChange(historyToSync.location);

    const subscribe = (listener?: SimpleLocationListener) => {
        const onStoreChange = () => {
            if (listener) {
                listener(store.location, historyToSync.action);
            }

            if (window.location.pathname !== store.location) {
                store.replace(store.location);
            }
        };

        const unsubscribeFromStore = observe(store, 'location', onStoreChange);

        if (listener) {
            listener(store.location, historyToSync.action);
        }

        return () => {
            unsubscribeFromStore();
        };
    };
    const unsubscribe = () => unsubscribeFromHistory();

    const newHistory = historyToSync as IHistorySubscriber;
    newHistory.subscribe = subscribe;
    newHistory.unsubscribe = unsubscribe;
    return newHistory;
};

export interface IRouterStore extends RouterStore {}
